import {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
  type ReactNode
} from 'react';
import { type IMapComponents, type YMapsStatus } from '@/types/IYMaps';
import { initYmaps } from '@/utils/ymaps/ymapsInit';

interface IYmapsContextValue {
  status: YMapsStatus;
  components: IMapComponents | null;
}

const YmapsContext = createContext<IYmapsContextValue | null>(null);

export const useYmaps = (): IYmapsContextValue => {
  const context = useContext(YmapsContext);
  if (context === null) {
    throw new Error('useYmaps должен использоваться внутри YmapsProvider');
  }
  return context;
};

interface YmapsProviderProps {
  children: ReactNode;
}

export const YmapsProvider: FC<YmapsProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<YMapsStatus>('loading');
  const [components, setComponents] = useState<IMapComponents | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initialize = async (): Promise<void> => {
      try {
        const result = await initYmaps();

        if (!isMounted) {
          return;
        }

        if (result === null) {
          setStatus('error');
          return;
        }

        const { reactify, clusterer, clusterByGrid } = result;
        const ymapsReactified = reactify.module(ymaps3);
        const clustererReactified = reactify.module(clusterer);

        setComponents({
          YMap: ymapsReactified.YMap,
          YMapDefaultSchemeLayer: ymapsReactified.YMapDefaultSchemeLayer,
          YMapDefaultFeaturesLayer: ymapsReactified.YMapDefaultFeaturesLayer,
          YMapMarker: ymapsReactified.YMapMarker,
          YMapListener: ymapsReactified.YMapListener,
          YMapClusterer: clustererReactified.YMapClusterer,
          clusterByGrid
        });
        setStatus('ready');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('YmapsProvider: ошибка инициализации SDK', error);
        if (isMounted) {
          setStatus('error');
        }
      }
    };

    void initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <YmapsContext.Provider value={{ status, components }}>
      {children}
    </YmapsContext.Provider>
  );
};
